import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { Select } from '@/components/synapse/Select';
import { StatusDot } from '@/components/synapse/StatusDot';
import { Button } from '@/components/ui/button';
import { useCan } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/lib/relativeTime';
import { orderStatusTone, translateOrderStatus, translateSpecimenType } from '@/lib/labTests';
import { translateApiError } from '@/lib/apiError';
import { ReportUploader } from '@/features/lab/ReportUploader';
import { formatFileSize } from '@/features/lab/labClient';
import {
  useCancelLabOrder,
  useCancelLabTest,
  useCollectSpecimen,
  useDeleteLabReport,
  useLabLaboratories,
  useLabOrder,
  useLabReportUrl,
  useRejectSpecimen,
  useUpdateLabOrder,
} from '@/features/lab/useLab';
import type { LabDocument, LabOrderDetail, LabRejectionReason, LabSpecimen, LabTest } from '@/types/lab';
import type { SpecimenType } from '@synapse/lab-tests';

const REJECTION_REASONS: LabRejectionReason[] = [
  'haemolysed',
  'insufficient',
  'clotted',
  'mislabelled',
  'leaked',
  'contaminated',
  'other',
];

const TONE_TO_DOT = {
  success: 'ok',
  info: 'warn',
  danger: 'danger',
  neutral: 'neutral',
  muted: 'neutral',
} as const;

/**
 * One requisition, end to end: who it is for, what was drawn, what was asked,
 * and what has come back.
 *
 * The screen's job that is easy to get wrong is making it obvious that rejecting
 * a sample REOPENS its tests. The server does that in the same transaction as
 * the rejection; the dialog says so before the click, the rejected sample stays
 * on the card struck through with its reason, and the sample type reappears in
 * the section's "Record sample" row — so the consequence is visible and the
 * remedy is the ordinary control rather than a special one.
 */
export function LabOrderDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const { orderId } = useParams<{ orderId: string }>();

  const order = useLabOrder(orderId);
  const [banner, setBanner] = useState<string | null>(null);

  if (order.isPending) {
    return <div className="mx-auto max-w-4xl px-4 py-6 text-[13.5px] text-muted">{t('common.loading')}</div>;
  }

  if (order.isError || !order.data) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col items-start gap-3 px-4 py-6">
        <p className="text-[13.5px] text-ink">{t('lab.detail.loadFailed')}</p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => void order.refetch()}>
            {t('common.retry')}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/lab')}>
            {t('lab.detail.back')}
          </Button>
        </div>
      </div>
    );
  }

  const data = order.data;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6">
      <button
        type="button"
        onClick={() => navigate('/lab')}
        className="inline-flex items-center gap-1 self-start text-[13px] text-muted hover:text-ink"
      >
        <Icon name="chevron_left" size={15} />
        {t('lab.detail.back')}
      </button>

      <OrderHeader order={data} canUpdate={can('lab.orders.update')} onNotice={setBanner} />

      {banner && (
        <p className="rounded-md border border-line-2 bg-paper-2 px-3 py-2 text-[12.5px] text-muted">
          {banner}
        </p>
      )}

      <SpecimensSection
        order={data}
        canCollect={can('lab.specimens.collect')}
        onNotice={setBanner}
      />

      <TestsSection
        order={data}
        canUpdate={can('lab.orders.update')}
        canUpload={can('lab.results.upload')}
        canReadResults={can('lab.results.read')}
        canDelete={can('lab.results.delete')}
        onNotice={setBanner}
      />
    </div>
  );
}

function OrderHeader({
  order,
  canUpdate,
  onNotice,
}: {
  order: LabOrderDetail;
  canUpdate: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const cancelOrder = useCancelLabOrder();

  // Once anything is resulted the API refuses; hiding the action stops the page
  // offering a button that only ever returns a 409.
  const anyResulted = order.tests.some((test) => test.status === 'resulted');
  const cancellable = canUpdate && !order.cancelledAt && !anyResulted;

  return (
    <header className="flex flex-col gap-2 rounded-md border border-line-2 bg-paper p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="font-mono text-[15px] font-semibold tabular-nums tracking-[-0.01em] text-ink">
            {order.accessionNumber}
          </span>
          <span className="text-[12.5px] text-muted">
            {formatRelativeTime(order.createdAt, t)}
            {order.orderingExternalLabel
              ? ` · ${t('lab.external')}: ${order.orderingExternalLabel}`
              : ''}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[13px] text-ink">
            <StatusDot tone={TONE_TO_DOT[orderStatusTone(order.status)]} />
            {translateOrderStatus(t, order.status)}
          </span>
          {order.priority === 'urgent' && (
            <span className="rounded-full bg-destructive px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
              {t('lab.priority.urgent')}
            </span>
          )}
          {cancellable && (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              {t('lab.detail.cancelOrder')}
            </Button>
          )}
        </div>
      </div>

      <LaboratoryLine order={order} canUpdate={canUpdate} onNotice={onNotice} />

      {order.indication && (
        <p className="text-[13px] text-ink">
          <span className="text-muted">{t('lab.detail.indication')}: </span>
          {order.indication}
        </p>
      )}

      {order.cancelledAt && order.cancellationReason && (
        <p className="text-[12.5px] text-destructive">
          {t('lab.detail.cancelledBecause', { reason: order.cancellationReason })}
        </p>
      )}

      {confirming && (
        <Modal
          title={t('lab.detail.cancelOrderTitle')}
          description={t('lab.detail.cancelOrderExplain')}
          onClose={() => setConfirming(false)}
          isPending={cancelOrder.isPending}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!reason.trim() || cancelOrder.isPending}
                onClick={() => {
                  cancelOrder.mutate(
                    { orderId: order.id, reason: reason.trim() },
                    {
                      onSuccess: () => setConfirming(false),
                      onError: (err) => onNotice(translateApiError(err)),
                    },
                  );
                }}
              >
                {t('lab.detail.cancelOrder')}
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-muted">
              {t('lab.detail.cancelReasonLabel')}
            </span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-11 rounded-md border border-line-2 bg-paper px-3 text-[13.5px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </Modal>
      )}
    </header>
  );
}

/**
 * Where this order's work is being sent, and — for anyone who can update the
 * order — a way to change it.
 *
 * An inline select rather than a modal: one field, reversible, non-clinical.
 * The cancel flow beside it takes a modal because it is irreversible and needs a
 * typed reason; a dropdown does not earn the same ceremony.
 */
function LaboratoryLine({
  order,
  canUpdate,
  onNotice,
}: {
  order: LabOrderDetail;
  canUpdate: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const laboratories = useLabLaboratories();
  const updateOrder = useUpdateLabOrder();

  const editable = canUpdate && !order.cancelledAt;

  // The order's CURRENT laboratory is always offered, even if the clinic has
  // since hidden it. Without this the select would render blank on such an
  // order and the first save would silently clear a real fact.
  const options = useMemo(() => {
    const active = laboratories.data ?? [];
    if (!order.laboratoryId || active.some((lab) => lab.id === order.laboratoryId)) {
      return active;
    }
    return [
      ...active,
      {
        id: order.laboratoryId,
        name: order.laboratoryName ?? t('lab.laboratories.hidden'),
        sortOrder: 0,
        active: false,
      },
    ];
  }, [laboratories.data, order.laboratoryId, order.laboratoryName, t]);

  if (!editable) {
    if (!order.laboratoryName) return null;
    return (
      <p className="text-[13px] text-ink">
        <span className="text-muted">{t('lab.laboratory')}: </span>
        {order.laboratoryName}
      </p>
    );
  }

  return (
    <label className="flex flex-wrap items-center gap-2 text-[13px]">
      <span className="text-muted">{t('lab.laboratory')}</span>
      <Select
        value={order.laboratoryId ?? ''}
        disabled={updateOrder.isPending}
        aria-label={t('lab.detail.changeLaboratory')}
        selectSize="sm"
        className="w-52"
        onChange={(e) => {
          onNotice(null);
          updateOrder.mutate(
            { orderId: order.id, input: { laboratoryId: e.target.value || null } },
            {
              onError: (err) =>
                onNotice(
                  translateApiError(err, { fallback: t('lab.detail.laboratorySaveFailed') }),
                ),
            },
          );
        }}
      >
        <option value="">{t('lab.laboratoryUnset')}</option>
        {options.map((lab) => (
          <option key={lab.id} value={lab.id}>
            {lab.active ? lab.name : `${lab.name} — ${t('lab.laboratories.hidden')}`}
          </option>
        ))}
      </Select>
    </label>
  );
}

function SpecimensSection({
  order,
  canCollect,
  onNotice,
}: {
  order: LabOrderDetail;
  canCollect: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const collect = useCollectSpecimen();

  // Types a live test still needs but has no live sample for. This is what the
  // "Record sample" buttons are derived from, so the page can never offer a draw
  // the order does not want or a duplicate of one already taken.
  const liveTypes = new Set(
    order.specimens.filter((s) => s.rejectedAt === null).map((s) => s.specimenType),
  );
  const neededTypes = [
    ...new Set(
      order.tests.filter((test) => test.status !== 'cancelled').map((test) => test.specimenType),
    ),
  ].filter((type) => !liveTypes.has(type));

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted">
        {t('lab.detail.specimens')}
      </h2>

      {order.specimens.length === 0 && (
        <p className="text-[13px] text-muted">{t('lab.detail.noSpecimens')}</p>
      )}

      <ul className="flex flex-col gap-2">
        {order.specimens.map((specimen) => (
          <li key={specimen.id}>
            <SpecimenCard specimen={specimen} canCollect={canCollect} onNotice={onNotice} />
          </li>
        ))}
      </ul>

      {canCollect && !order.cancelledAt && neededTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {neededTypes.map((type) => (
            <Button
              key={type}
              size="sm"
              variant="secondary"
              disabled={collect.isPending}
              onClick={() =>
                collect.mutate(
                  {
                    orderId: order.id,
                    specimenType: type as SpecimenType,
                    collectedAt: new Date().toISOString(),
                  },
                  {
                    // Without this the common failure — a colleague recorded the
                    // same draw first, so the partial unique index refuses it —
                    // un-disables the button, changes nothing on screen and says
                    // nothing, which reads as "the click didn't register".
                    onSuccess: () => onNotice(null),
                    onError: (err) => onNotice(translateApiError(err)),
                  },
                )
              }
            >
              {t('lab.detail.collectType', { type: translateSpecimenType(t, type as SpecimenType) })}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One drawn sample.
 *
 * There is no "Re-collect" button here on purpose. Rejecting drops the type out
 * of the order's live set, so the section above grows a "Record <type> sample"
 * button for it — the same control used for a first draw, because it is the same
 * operation. A second button here would be a second way to spell one action.
 */
function SpecimenCard({
  specimen,
  canCollect,
  onNotice,
}: {
  specimen: LabSpecimen;
  canCollect: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState<LabRejectionReason>('haemolysed');
  const [note, setNote] = useState('');
  const reject = useRejectSpecimen();
  const rejected = specimen.rejectedAt !== null;

  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2.5 ${
        rejected ? 'border-line-2 bg-paper-2 opacity-70' : 'border-line-2 bg-paper'
      }`}
    >
      <span
        className={`text-[13.5px] font-medium text-ink ${rejected ? 'line-through' : ''}`}
      >
        {translateSpecimenType(t, specimen.specimenType)}
      </span>
      {specimen.container && (
        <span className="text-[12px] text-muted">{specimen.container}</span>
      )}
      <span className="flex-1 text-[12px] text-muted">
        {t('lab.collectedAt')} {formatRelativeTime(specimen.collectedAt, t)}
      </span>

      {rejected && specimen.rejectionReason && (
        <span className="text-[12px] text-destructive">
          {t('lab.detail.rejectedBecause', {
            reason: t(`lab.detail.reason.${specimen.rejectionReason}`),
          })}
        </span>
      )}

      {!rejected && canCollect && (
        <Button size="sm" variant="ghost" onClick={() => setRejecting(true)}>
          {t('lab.detail.reject')}
        </Button>
      )}

      {rejecting && (
        <Modal
          title={t('lab.detail.rejectTitle', {
            type: translateSpecimenType(t, specimen.specimenType),
          })}
          // Stated before the click, because the consequence is not obvious from
          // the word "reject": the tests drawn on this sample go back into the
          // collection queue.
          description={t('lab.detail.rejectExplain')}
          onClose={() => setRejecting(false)}
          isPending={reject.isPending}
          footer={
            <>
              <Button variant="secondary" onClick={() => setRejecting(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={reject.isPending}
                onClick={() =>
                  reject.mutate(
                    {
                      specimenId: specimen.id,
                      reason,
                      ...(note.trim() ? { note: note.trim() } : {}),
                    },
                    {
                      onSuccess: () => {
                        setRejecting(false);
                        onNotice(null);
                      },
                      // A rejection that 409s (someone rejected it first) or
                      // 403s left the modal open with a live button and no
                      // explanation. The banner sits behind this modal, so close
                      // it first — otherwise the message is reported to a screen
                      // the operator cannot see.
                      onError: (err) => {
                        setRejecting(false);
                        onNotice(translateApiError(err));
                      },
                    },
                  )
                }
              >
                {t('lab.detail.rejectConfirm')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-muted">
                {t('lab.detail.rejectReason')}
              </span>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value as LabRejectionReason)}
              >
                {REJECTION_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`lab.detail.reason.${r}`)}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-muted">{t('lab.detail.rejectNote')}</span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-11 rounded-md border border-line-2 bg-paper px-3 text-[13.5px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TestsSection({
  order,
  canUpdate,
  canUpload,
  canReadResults,
  canDelete,
  onNotice,
}: {
  order: LabOrderDetail;
  canUpdate: boolean;
  canUpload: boolean;
  canReadResults: boolean;
  canDelete: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted">
        {t('lab.detail.tests')}
      </h2>
      <ul className="flex flex-col gap-2">
        {order.tests.map((test) => (
          <li key={test.id}>
            <TestRow
              order={order}
              test={test}
              canUpdate={canUpdate}
              canUpload={canUpload}
              canReadResults={canReadResults}
              canDelete={canDelete}
              onNotice={onNotice}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function TestRow({
  order,
  test,
  canUpdate,
  canUpload,
  canReadResults,
  canDelete,
  onNotice,
}: {
  order: LabOrderDetail;
  test: LabTest;
  canUpdate: boolean;
  canUpload: boolean;
  canReadResults: boolean;
  canDelete: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const cancelTest = useCancelLabTest();

  const document = order.documents.find((d) => d.id === test.resultDocumentId) ?? null;
  const cancelled = test.status === 'cancelled';

  // The last live test cannot be cancelled (the API refuses — cancel the order
  // instead), so the action is hidden rather than offered and rejected.
  const liveCount = order.tests.filter((x) => x.status !== 'cancelled').length;
  const canCancelThis = canUpdate && !cancelled && test.status !== 'resulted' && liveCount > 1;

  return (
    <div
      className={`flex flex-col gap-2 rounded-md border border-line-2 px-3 py-2.5 ${
        cancelled ? 'bg-paper-2 opacity-70' : 'bg-paper'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className={`text-[13.5px] font-medium text-ink ${cancelled ? 'line-through' : ''}`}>
          {test.name}
        </span>
        <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11.5px] text-muted">
          {translateSpecimenType(t, test.specimenType)}
        </span>
        <span className="flex-1" />

        {cancelled && test.cancelReason && (
          <span className="text-[12px] text-muted">
            {t('lab.detail.cancelledBecause', { reason: test.cancelReason })}
          </span>
        )}

        {!cancelled && test.status === 'pending' && (
          <span className="text-[12px] text-muted">{t('lab.detail.awaitingReport')}</span>
        )}

        {canCancelThis && (
          <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
            {t('lab.detail.cancelTest')}
          </Button>
        )}
      </div>

      {document && (
        <ReportRow
          document={document}
          canReadResults={canReadResults}
          canDelete={canDelete}
          onNotice={onNotice}
        />
      )}

      {!cancelled && canUpload && !order.cancelledAt && (
        <ReportUploader
          orderId={order.id}
          testId={test.id}
          supersedesDocumentId={test.resultDocumentId}
          onUploaded={(_, duplicateOf) =>
            onNotice(duplicateOf ? t('lab.detail.duplicateWarning') : null)
          }
        />
      )}

      {cancelling && (
        <Modal
          title={t('lab.detail.cancelTestTitle', { test: test.name })}
          onClose={() => setCancelling(false)}
          isPending={cancelTest.isPending}
          footer={
            <>
              <Button variant="secondary" onClick={() => setCancelling(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!reason.trim() || cancelTest.isPending}
                onClick={() =>
                  cancelTest.mutate(
                    { orderId: order.id, testId: test.id, reason: reason.trim() },
                    {
                      onSuccess: () => setCancelling(false),
                      onError: (err) => onNotice(translateApiError(err)),
                    },
                  )
                }
              >
                {t('lab.detail.cancelTest')}
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-muted">
              {t('lab.detail.cancelReasonLabel')}
            </span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-11 rounded-md border border-line-2 bg-paper px-3 text-[13.5px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </Modal>
      )}
    </div>
  );
}

function ReportRow({
  document: doc,
  canReadResults,
  canDelete,
  onNotice,
}: {
  document: LabDocument;
  canReadResults: boolean;
  canDelete: boolean;
  onNotice: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const mintUrl = useLabReportUrl();
  const removeReport = useDeleteLabReport();

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md bg-paper-2 px-3 py-2">
      <Icon name="file" size={15} className="text-muted" />
      <span className="text-[13px] text-ink">{doc.fileName}</span>
      <span className="text-[12px] tabular-nums text-muted">{formatFileSize(doc.byteSize)}</span>
      {doc.reportDate && <span className="text-[12px] text-muted">{doc.reportDate}</span>}
      <span className="flex-1" />

      {canReadResults ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={mintUrl.isPending}
          onClick={() =>
            mintUrl.mutate(doc.id, {
              // Opened in a new tab rather than an inline <object>: the URL
              // lives 120 seconds, and a tab the user controls is the one place
              // an expiry is recoverable by simply asking again.
              onSuccess: ({ url }) => window.open(url, '_blank', 'noopener,noreferrer'),
              onError: (err) =>
                onNotice(translateApiError(err, { fallback: t('lab.detail.openFailed') })),
            })
          }
        >
          {t('lab.openReport')}
        </Button>
      ) : (
        // The API refuses the mint for this member too; saying so beats a button
        // that always fails.
        <span className="text-[12px] text-muted">{t('lab.noReportAccess')}</span>
      )}

      {canDelete && (
        <Button size="sm" variant="ghost" onClick={() => setConfirmingRemove(true)}>
          {t('lab.detail.removeReport')}
        </Button>
      )}

      {confirmingRemove && (
        <Modal
          title={t('lab.detail.removeReportTitle')}
          // The test reverting to "awaiting a report" is the part people do not
          // expect, so it is said before the click rather than discovered after.
          description={t('lab.detail.removeReportExplain')}
          onClose={() => setConfirmingRemove(false)}
          isPending={removeReport.isPending}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmingRemove(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={removeReport.isPending}
                onClick={() =>
                  removeReport.mutate(
                    { documentId: doc.id },
                    {
                      onSuccess: () => setConfirmingRemove(false),
                      onError: (err) => onNotice(translateApiError(err)),
                    },
                  )
                }
              >
                {t('lab.detail.removeReport')}
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-muted">{doc.fileName}</p>
        </Modal>
      )}
    </div>
  );
}
